/**
 * src/i18n/language-pack-validator.ts
 *
 * Strict validation engine for imported Language Packs.
 *
 * Checks:
 * 1. Schema structure: code, name, direction, version, schemaVersion, translations.
 * 2. Injection rejection: forbids raw HTML, `<script>`, `onerror=`, `onclick=`, etc.
 * 3. Prototype pollution protection: forbids `__proto__`, `constructor`, `prototype`.
 * 4. Schema versioning: warns if pack version does not match current app schema.
 */

export const CURRENT_I18N_SCHEMA_VERSION = '2.1.0';

export interface LanguagePack {
  code: string;
  name: string;
  direction: 'ltr' | 'rtl';
  version: string;
  schemaVersion: string;
  translations: Record<string, string>;
}

export interface ValidationResult {
  valid: boolean;
  errors: string[];
  warnings: string[];
  sanitizedPack?: LanguagePack;
}

const FORBIDDEN_HTML_PATTERN = /<\s*script|onerror\s*=|onclick\s*=|onload\s*=|<\s*iframe|<\s*embed|<\s*object/i;
const PROTOTYPE_POLLUTION_KEYS = new Set(['__proto__', 'constructor', 'prototype']);

export class LanguagePackValidator {
  public static validate(rawJson: unknown): ValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    if (!rawJson || typeof rawJson !== 'object' || Array.isArray(rawJson)) {
      return { valid: false, errors: ['Language pack must be a valid JSON object.'], warnings };
    }

    const obj = rawJson as Record<string, any>;

    // 1. Validate required metadata fields
    if (!obj.code || typeof obj.code !== 'string' || obj.code.trim().length === 0) {
      errors.push('Missing or invalid "code" field (must be non-empty string).');
    }

    if (!obj.name || typeof obj.name !== 'string' || obj.name.trim().length === 0) {
      errors.push('Missing or invalid "name" field (must be non-empty string).');
    }

    const direction = obj.direction === 'rtl' ? 'rtl' : 'ltr';
    if (obj.direction && obj.direction !== 'ltr' && obj.direction !== 'rtl') {
      errors.push('"direction" must be either "ltr" or "rtl".');
    }

    const version = typeof obj.version === 'string' ? obj.version : '1.0.0';
    const schemaVersion = typeof obj.schemaVersion === 'string' ? obj.schemaVersion : '1.0.0';

    if (schemaVersion !== CURRENT_I18N_SCHEMA_VERSION) {
      warnings.push(
        `Pack schemaVersion (${schemaVersion}) does not match current app version (${CURRENT_I18N_SCHEMA_VERSION}). Missing keys will fall back to default English.`
      );
    }

    // 2. Validate translations map
    if (!obj.translations || typeof obj.translations !== 'object' || Array.isArray(obj.translations)) {
      errors.push('Missing or invalid "translations" object dictionary.');
      return { valid: false, errors, warnings };
    }

    const sanitizedTranslations: Record<string, string> = {};

    for (const [key, value] of Object.entries(obj.translations)) {
      if (PROTOTYPE_POLLUTION_KEYS.has(key)) {
        errors.push(`Forbidden prototype pollution key detected: "${key}".`);
        continue;
      }

      if (typeof value !== 'string') {
        errors.push(`Translation key "${key}" must have a string value, got ${typeof value}.`);
        continue;
      }

      // Check for dangerous scripts or injections
      if (FORBIDDEN_HTML_PATTERN.test(value)) {
        errors.push(`Translation key "${key}" contains forbidden script or event handler pattern.`);
        continue;
      }

      sanitizedTranslations[key] = value;
    }

    if (errors.length > 0) {
      return { valid: false, errors, warnings };
    }

    const sanitizedPack: LanguagePack = {
      code: obj.code.trim(),
      name: obj.name.trim(),
      direction,
      version,
      schemaVersion,
      translations: sanitizedTranslations,
    };

    return {
      valid: true,
      errors: [],
      warnings,
      sanitizedPack,
    };
  }
}
